// src/lib/historyCompatibility.ts

export type HistoryCompatibilityStatus = "baseline" | "comparable" | "directional";

export type HistoryCompatibilityResult = {
  status: HistoryCompatibilityStatus;
  matchingCategoryCount: number;
  currentCategoryCount: number;
  comparisonCategoryCount: number;
  promptCountsMatch: boolean;
  message: string;
};

function normalizeCategoryKey(category?: string | null): string {
  return (category || "uncategorized")
    .toLowerCase()
    .replace(/^project_/, "")
    .replace(/^local_/, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

export function getHistoryCompatibility(params: {
  currentCategories: Array<string | null | undefined>;
  comparisonCategories?: Array<string | null | undefined>;
  currentPromptCount: number;
  comparisonPromptCount?: number | null;
}): HistoryCompatibilityResult {
  const currentSet = new Set(params.currentCategories.map(normalizeCategoryKey));
  const comparisonSet = new Set((params.comparisonCategories || []).map(normalizeCategoryKey));

  const currentCategoryCount = currentSet.size;
  const comparisonCategoryCount = comparisonSet.size;
  const matchingCategoryCount = Array.from(currentSet).filter((category) =>
    comparisonSet.has(category)
  ).length;

  const promptCountsMatch =
    params.comparisonPromptCount != null &&
    params.currentPromptCount === params.comparisonPromptCount;

  if (!params.comparisonPromptCount || comparisonCategoryCount === 0) {
    return {
      status: "baseline",
      matchingCategoryCount,
      currentCategoryCount,
      comparisonCategoryCount,
      promptCountsMatch,
      message:
        "Baseline captured. After the next completed run, this module will show movement, gains, losses, and category-level deltas.",
    };
  }

  const overlapRate =
    currentCategoryCount > 0 ? matchingCategoryCount / currentCategoryCount : 0;

  if (promptCountsMatch && overlapRate >= 0.8) {
    return {
      status: "comparable",
      matchingCategoryCount,
      currentCategoryCount,
      comparisonCategoryCount,
      promptCountsMatch,
      message:
        "This comparison uses comparable prompt groups and can be used for category-level trend analysis.",
    };
  }

  return {
    status: "directional",
    matchingCategoryCount,
    currentCategoryCount,
    comparisonCategoryCount,
    promptCountsMatch,
    message:
      "This comparison uses different prompt sets. Top-line trends are directional; category-level deltas may not be comparable.",
  };
}